import { useContext, useEffect, useState } from 'react';
import { GameDocumentContext } from '../../../contexts/game-document';
import Toolbar from '../toolbar';
import {
  DeleteTimerAsync,
  UpdateGameDocState,
  GetResourceEntity,
  SaveTimerEntityEditorAsync,
  GetResourceValue
} from '../../../utils/game-document';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { TimerEntity, ResourceEntity } from '../../../types/game-document/';
import { FluidForm } from '../../../components/forms';
import { uuid } from '../../../types/common-helper';
import { TimerEditorFull } from '../../../features/game-document/timers/timer-editor-full';
import { ResourceType } from '../../../types/game-document/resource-type';
import cloneDeep from 'lodash.clonedeep';
import RequiredFields from '../../../types/required-fields';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import { ActionTopRow } from '../../../components/game-designer/action-top-row';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { useToggle } from 'usehooks-ts';
import { useBreadcrumb } from '../../../contexts/breadcrumb';

const Timer = () => {
  const { timerId } = useParams();
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();
  const getEntityById = (entityId: string) =>
    state.gameDocument?.assets.timers?.find((i) => i.id === entityId)!;
  const [entity, setEntity] = useState<TimerEntity>(() =>
    cloneDeep(getEntityById(timerId!))
  );
  const [resources, setResources] = useState<ResourceEntity[]>([]);
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TimerEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);

  const onDeleteEntity = async (entityId: string) => {
    DeleteTimerAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        navigate('../timers');
      }
    );
  };

  const updateEntityAsync = async (
    name: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => {
    setRequiredFields((prev) =>
      prev.map((task: RequiredFields<TimerEntity>) => {
        const copyTask = cloneDeep({ ...task });
        if (task.name === name) {
          copyTask.errorMessage = '';
          if (name === 'name') {
            const duplicateValueIndex: number =
              state.gameDocument?.assets?.timers
                ?.filter((item: TimerEntity) => item.id !== entity.id)
                .findIndex(
                  (item: TimerEntity) =>
                    item.name.toLowerCase() === (value as string)?.toLowerCase()
                ) ?? -1;
            if (value === '') {
              copyTask.errorMessage = ERR_INPUT_REQUIRED;
            } else if (duplicateValueIndex !== -1) {
              copyTask.errorMessage = ERR_DUPLICATE_NAME_VALUE;
            }
          } else if (name === 'titleResId') {
            if (value === '') {
              copyTask.errorMessage = ERR_INPUT_REQUIRED;
            }
          }
        }
        return copyTask;
      })
    );
    let newEntity: TimerEntity = cloneDeep({ ...entity });
    let newResources: ResourceEntity[] = [...resources];
    if (!name?.toLowerCase().includes('resid')) {
      newEntity[name] = value as never;
    } else {
      const resourceId = entity[name];
      const resIndex: number = newResources?.findIndex(
        (x) => x.id === resourceId
      );
      if (resIndex === -1) {
        const newId: string = uuid();
        let resourceEntity: ResourceEntity = {
          id: newId,
          description: '',
          name: name,
          type: type,
          value: value as string
        };
        newResources.push(resourceEntity);

        newEntity[name] = newId as never;
      } else {
        newResources[resIndex].value = value as never;
        newEntity[name] = resourceId as never;
      }
    }
    submitEntityAsync(newEntity, newResources);
  };

  const submitEntityAsync = async (
    newEntity: TimerEntity,
    newResources: ResourceEntity[]
  ) => {
    const entityResources = newResources?.map((item) => ({
      isNew: false,
      entity: item
    })) as EntityEditor<ResourceEntity>[];

    setEntity(newEntity);
    setResources(newResources);

    let response = await SaveTimerEntityEditorAsync(
      state?.gameDocument!,
      { isNew: false, entity: newEntity },
      entityResources,
      false
    );
    setState((prev) => UpdateGameDocState(prev, response));
  };

  useEffect(() => {
    if (entity) {
      // Set the page title.
      document.title = `${GetResourceValue(state.gameDocument!, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
    }
  }, [state, entity]);

  useEffect(() => {
    setResources([GetResourceEntity(state?.gameDocument!, entity.titleResId!)]);
  }, [timerId]);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const getTimerName = (timerId: string | undefined) => {
    if (timerId) {
      const timer = state.gameDocument?.assets.timers?.find(
        (i) => i.id === timerId
      )!;
      if (timer) {
        return GetResourceValue(state.gameDocument!, timer.titleResId ?? '');
      }
    }
  };

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/timers/*', text: 'Timers' },
      {
        path: '/designer/:gameId/assets/timers/:timerId/*',
        text: getTimerName(timerId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname, state.gameDocument]);

  return (
    <>
      <Toolbar
        title={GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}
        buttonHelpSupport={{
          title: 'Timers guide',
          url: 'https://forum.catalystglobal.com/t/4531'
        }}
      />
      <ActionTopRow onDeleteClick={setShowConfirmationDelete} />

      <TimerEditorFull
        timer={entity}
        resources={resources}
        requiredFields={requiredFields}
        handleEntityChange={(
          name: keyof TimerEntity,
          value: string | number,
          type: ResourceType,
          isClearValue?: boolean
        ) => {
          updateEntityAsync(name, value, type, isClearValue);
        }}
        handleEventAction={(entity) => submitEntityAsync(entity, resources)}
      />

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the timer?
        </YesNoDialog>
      )}
    </>
  );
};

export default Timer;
